import { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Package, Tag, Heart, TrendingUp, ArrowRight, Plus, Gavel, ArrowLeftRight, Trash2, Star, Settings, MessageSquare, BookOpen, BadgeCheck, Layers, ShieldCheck, AlertTriangle } from 'lucide-react';
import { useCurrentUser, useFeaturedCollection, useUserListings, useDeleteListing, useUserReviews, useDecks, useUserLevel } from '@/hooks/useApi';
import { formatPrice } from '@/utils';
import { Review } from '@/types';
import UserAvatar from '@/components/UserAvatar';
import AddCardModal from '@/components/AddCardModal';
import QuickListingModal from '@/components/QuickListingModal';
import CardImage from '@/components/CardImage';

// ── Card Fan / Deck component ─────────────────────────────────────────────────
// Cards spread in a fan like a hand. The "active" card is the front/center one.
// Auto-cycles every 2.5s. Pauses on hover. Clicking a side card brings it front;
// clicking the front card navigates.

const FAN_CONFIG = [
  { rotate: -22, x: -68, y: 10, scale: 0.70, z: 1, opacity: 0.60 }, // offset -2
  { rotate: -11, x: -34, y: 4,  scale: 0.82, z: 2, opacity: 0.78 }, // offset -1
  { rotate:   0, x:   0, y: 0,  scale: 1.00, z: 5, opacity: 1.00 }, // offset  0 (front)
  { rotate:  11, x:  34, y: 4,  scale: 0.82, z: 2, opacity: 0.78 }, // offset +1
  { rotate:  22, x:  68, y: 10, scale: 0.70, z: 1, opacity: 0.60 }, // offset +2
];

function CardFan({
  cards,
  onSell,
}: {
  cards: any[];
  onSell: (item: any) => void;
}) {
  const [activeIdx, setActiveIdx] = useState(0);
  const pauseRef    = useRef(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const prevLenRef  = useRef(cards.length);

  const n = cards.length;

  const advance = useCallback(() => {
    setActiveIdx(p => (p + 1) % n);
  }, [n]);

  // Reset to 0 when the card list changes (user updated Top 5)
  useEffect(() => {
    if (cards.length !== prevLenRef.current) {
      setActiveIdx(0);
      prevLenRef.current = cards.length;
    }
  }, [cards]);

  // Auto-cycle
  useEffect(() => {
    if (n <= 1) return;
    intervalRef.current = setInterval(() => {
      if (!pauseRef.current) advance();
    }, 2500);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [advance, n]);

  if (n === 0) return null;

  // For a single card, show it centered with no fan
  const getStyle = (cardIdx: number): React.CSSProperties => {
    let offset = cardIdx - activeIdx;
    // Wrap to shortest path
    if (offset >  n / 2) offset -= n;
    if (offset < -n / 2) offset += n;

    // Clamp to fan range — cards outside ±2 are invisible
    const absOff = Math.abs(offset);
    if (absOff > 2) {
      return { opacity: 0, pointerEvents: 'none', zIndex: 0, position: 'absolute', top: 0, left: '50%' };
    }

    const cfg = FAN_CONFIG[offset + 2]; // offset -2..+2 → index 0..4
    return {
      position:        'absolute',
      top:             0,
      left:            '50%',
      zIndex:          cfg.z,
      opacity:         cfg.opacity,
      cursor:          offset === 0 ? 'pointer' : 'pointer',
      transformOrigin: 'bottom center',
      transform:       `translateX(calc(-50% + ${cfg.x}px)) translateY(${cfg.y}px) rotate(${cfg.rotate}deg) scale(${cfg.scale})`,
      transition:      'transform 0.45s cubic-bezier(0.34, 1.2, 0.64, 1), opacity 0.45s ease',
      willChange:      'transform',
    };
  };

  const frontCard = cards[activeIdx];

  return (
    <div
      className="relative flex flex-col items-center"
      onMouseEnter={() => { pauseRef.current = true; }}
      onMouseLeave={() => { pauseRef.current = false; }}
    >
      {/* Fan stage — fixed height so it doesn't shift layout */}
      <div className="relative w-full" style={{ height: '148px' }}>
        {cards.map((item: any, cardIdx: number) => {
          const isActive = cardIdx === activeIdx;
          const href = item.listing_status === 'active' && item.listing_id
            ? `/listings/${item.listing_id}`
            : `/cards/${item.card_id ?? item.card?.id}`;

          return (
            <div
              key={item.id}
              style={getStyle(cardIdx)}
              onClick={() => {
                if (!isActive) {
                  // Bring to front instead of navigating
                  setActiveIdx(cardIdx);
                  if (intervalRef.current) clearInterval(intervalRef.current);
                  intervalRef.current = setInterval(() => {
                    if (!pauseRef.current) advance();
                  }, 2500);
                }
              }}
            >
              <div
                className="rounded-xl overflow-hidden bg-gray-100 dark:bg-zinc-800"
                style={{ width: '80px', height: '112px', boxShadow: isActive ? '0 12px 32px rgba(0,0,0,0.28)' : '0 4px 12px rgba(0,0,0,0.18)' }}
              >
                <CardImage
                  src={item.listing_image_urls?.[0]}
                  alt={item.card?.name}
                  cardName={item.card?.name}
                  cardNumber={item.card?.card_number}
                  game={item.card?.game}
                  className="w-full h-full"
                />
              </div>
              {/* Listed badge on active card */}
              {isActive && item.listing_status === 'active' && (
                <div className="absolute top-1 left-1 bg-emerald-600 text-white text-[9px] font-bold rounded-full px-1.5 py-0.5 leading-tight">
                  Listed
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Front card info + actions */}
      {frontCard && (
        <div className="mt-3 text-center w-full px-2">
          <Link
            to={
              frontCard.listing_status === 'active' && frontCard.listing_id
                ? `/listings/${frontCard.listing_id}`
                : `/cards/${frontCard.card_id ?? frontCard.card?.id}`
            }
            className="font-semibold text-sm text-gray-900 dark:text-zinc-100 hover:text-brand-600 dark:text-brand-400 truncate block transition-colors"
          >
            {frontCard.card?.name}
          </Link>
          <p className="text-xs text-gray-500 dark:text-zinc-500 truncate">{frontCard.card?.set_name}</p>
          <div className="mt-2 flex gap-1.5 justify-center">
            {frontCard.listing_status === 'active' && frontCard.listing_id ? (
              <Link
                to={`/listings/${frontCard.listing_id}`}
                className="px-3 py-1 rounded-lg text-xs font-semibold bg-brand-600 text-white hover:bg-brand-700 transition-colors"
              >
                View Listing
              </Link>
            ) : (
              <button
                onClick={() => onSell(frontCard)}
                className="px-3 py-1 rounded-lg text-xs font-semibold bg-emerald-600 text-white hover:bg-emerald-700 transition-colors"
              >
                Sell
              </button>
            )}
          </div>
        </div>
      )}

      {/* Dot indicators */}
      {n > 1 && (
        <div className="flex gap-1.5 mt-3">
          {cards.map((_, i) => (
            <button
              key={i}
              onClick={() => setActiveIdx(i)}
              className={`rounded-full transition-all duration-300 ${
                i === activeIdx
                  ? 'w-4 h-1.5 bg-brand-600'
                  : 'w-1.5 h-1.5 bg-gray-300 hover:bg-gray-400'
              }`}
              aria-label={`Card ${i + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function DashboardPage() {
  const { data: user } = useCurrentUser();
  const { data: userLevel } = useUserLevel(user?.id);
  const { data: featuredCards = [], refetch: refetchCollection } = useFeaturedCollection();
  const { data: listings } = useUserListings(undefined);
  const { mutate: deleteListing } = useDeleteListing();
  const { data: reviews = [] } = useUserReviews(user?.id ?? '');

  const { data: decks = [] } = useDecks();

  const [showAddCard, setShowAddCard]       = useState(false);
  const [quickListItem, setQuickListItem]   = useState<any | null>(null);

  const recentCards = (featuredCards as any[]).slice(0, 5);
  const activeListings = Array.isArray(listings) ? listings.slice(0, 4) : [];
  const averageReviewRating =
    reviews.length > 0
      ? reviews.reduce((sum: number, review: Review) => sum + review.rating, 0) / reviews.length
      : null;
  const displayRating =
    typeof user?.rating === 'number'
      ? user.rating
      : typeof user?.seller_rating === 'number' && user.seller_rating > 0
        ? user.seller_rating
        : averageReviewRating;

  return (
    <div className="bg-gray-50 dark:bg-zinc-900 min-h-screen">
      {/* Header */}
      <div className="bg-white dark:bg-zinc-900 border-b border-gray-200 dark:border-zinc-800 text-white py-10">
        <div className="container-custom flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Link to="/profile" className="shrink-0 ring-2 ring-white/40 dark:ring-zinc-700 rounded-full hover:ring-white/80 dark:ring-brand-500 transition-all overflow-visible">
              <UserAvatar
                username={user?.username ?? '?'}
                avatarUrl={user?.avatar_url}
                size="lg"
                level={userLevel?.level}
                trophiesHref="/trophies"
              />
            </Link>
            <div>
              <h1 className="text-3xl font-display font-bold flex items-center gap-2">
                Welcome back, {user?.username ?? 'Collector'} 👋
                {user?.email_verified && (
                  <BadgeCheck className="h-6 w-6 text-green-400" title="Verified email" />
                )}
              </h1>
              <p className="text-gray-600 dark:text-zinc-400">Here's what's happening with your collection</p>
            </div>
          </div>
          <Link
            to="/settings"
            className="shrink-0 flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-100 dark:bg-zinc-800 hover:bg-gray-200 dark:hover:bg-zinc-700 dark:bg-zinc-700 text-gray-700 dark:text-zinc-300 text-sm font-medium transition-colors border border-gray-300 dark:border-zinc-700"
          >
            <Settings className="h-4 w-4" />
            Settings
          </Link>
        </div>
      </div>

      <div className="container-custom py-8">
        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <div className="bg-white dark:bg-zinc-900 rounded-xl border border-gray-200 dark:border-zinc-800 shadow-sm p-4">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-brand-500/20 rounded-lg">
                <Package className="h-5 w-5 text-brand-600 dark:text-brand-400" />
              </div>
              <p className="text-sm text-gray-600 dark:text-zinc-400">Collection</p>
            </div>
            <p className="text-2xl font-bold text-gray-900 dark:text-zinc-100">{user?.collection_count ?? 0}</p>
            <p className="text-xs text-gray-400 dark:text-zinc-500 mt-0.5">Total cards</p>
          </div>

          <div className="bg-white dark:bg-zinc-900 rounded-xl border border-gray-200 dark:border-zinc-800 shadow-sm p-4">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-emerald-500/20 rounded-lg">
                <Tag className="h-5 w-5 text-emerald-400" />
              </div>
              <p className="text-sm text-gray-600 dark:text-zinc-400">Active Listings</p>
            </div>
            <p className="text-2xl font-bold text-gray-900 dark:text-zinc-100">{Array.isArray(listings) ? listings.length : 0}</p>
            <p className="text-xs text-gray-400 dark:text-zinc-500 mt-0.5">For sale / trade</p>
          </div>

          <div className="bg-white dark:bg-zinc-900 rounded-xl border border-gray-200 dark:border-zinc-800 shadow-sm p-4">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-blue-500/20 rounded-lg">
                <TrendingUp className="h-5 w-5 text-blue-400" />
              </div>
              <p className="text-sm text-gray-600 dark:text-zinc-400">Total Sales</p>
            </div>
            <p className="text-2xl font-bold text-gray-900 dark:text-zinc-100">{user?.total_sales ?? 0}</p>
            <p className="text-xs text-gray-400 dark:text-zinc-500 mt-0.5">Completed</p>
          </div>

          <div className="bg-white dark:bg-zinc-900 rounded-xl border border-gray-200 dark:border-zinc-800 shadow-sm p-4">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-purple-500/20 rounded-lg">
                <ArrowLeftRight className="h-5 w-5 text-purple-400" />
              </div>
              <p className="text-sm text-gray-600 dark:text-zinc-400">Trades</p>
            </div>
            <p className="text-2xl font-bold text-gray-900 dark:text-zinc-100">{user?.total_trades ?? 0}</p>
            <p className="text-xs text-gray-400 dark:text-zinc-500 mt-0.5">Completed</p>
          </div>

          <div className="bg-white dark:bg-zinc-900 rounded-xl border border-gray-200 dark:border-zinc-800 shadow-sm p-4">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-yellow-500/20 rounded-lg">
                <Star className="h-5 w-5 text-yellow-400 fill-yellow-400" />
              </div>
              <p className="text-sm text-gray-600 dark:text-zinc-400">Rating</p>
            </div>
            <p className="text-2xl font-bold text-gray-900 dark:text-zinc-100">{displayRating !== null ? displayRating.toFixed(1) : '—'}</p>
            <p className="text-xs text-gray-400 dark:text-zinc-500 mt-0.5">Seller score</p>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3 mb-8">
          <button
            onClick={() => setShowAddCard(true)}
            className="flex items-center gap-3 bg-emerald-600 text-white rounded-xl p-4 hover:bg-emerald-700 transition-colors"
          >
            <BookOpen className="h-5 w-5" />
            <span className="font-medium text-sm">Add Card</span>
          </button>
          <Link to="/listings/create" className="flex items-center gap-3 bg-brand-600 text-white rounded-xl p-4 hover:bg-brand-700 transition-colors">
            <Plus className="h-5 w-5" />
            <span className="font-medium text-sm">New Listing</span>
          </Link>
          <Link to="/collection" className="flex items-center gap-3 bg-indigo-600 text-white rounded-xl p-4 hover:bg-indigo-700 transition-colors">
            <Layers className="h-5 w-5" />
            <span className="font-medium text-sm">Deck Builder</span>
          </Link>
          <Link to="/explore" className="flex items-center gap-3 bg-gray-100 dark:bg-zinc-800 border border-gray-300 dark:border-zinc-700 rounded-xl p-4 hover:border-brand-500 hover:bg-gray-200 dark:hover:bg-zinc-700 dark:bg-zinc-700 transition-colors">
            <Package className="h-5 w-5 text-brand-600 dark:text-brand-400" />
            <span className="font-medium text-sm text-gray-700 dark:text-zinc-300">Explore Cards</span>
          </Link>
          <Link to="/marketplace" className="flex items-center gap-3 bg-gray-100 dark:bg-zinc-800 border border-gray-300 dark:border-zinc-700 rounded-xl p-4 hover:border-brand-500 hover:bg-gray-200 dark:hover:bg-zinc-700 dark:bg-zinc-700 transition-colors">
            <Gavel className="h-5 w-5 text-orange-400" />
            <span className="font-medium text-sm text-gray-700 dark:text-zinc-300">Marketplace</span>
          </Link>
          <Link to="/wishlist" className="flex items-center gap-3 bg-gray-100 dark:bg-zinc-800 border border-gray-300 dark:border-zinc-700 rounded-xl p-4 hover:border-brand-500 hover:bg-gray-200 dark:hover:bg-zinc-700 dark:bg-zinc-700 transition-colors">
            <Heart className="h-5 w-5 text-pink-400" />
            <span className="font-medium text-sm text-gray-700 dark:text-zinc-300">Wishlist</span>
          </Link>
          <Link to="/forum" className="flex items-center gap-3 bg-gray-100 dark:bg-zinc-800 border border-gray-300 dark:border-zinc-700 rounded-xl p-4 hover:border-brand-500 hover:bg-gray-200 dark:hover:bg-zinc-700 dark:bg-zinc-700 transition-colors">
            <MessageSquare className="h-5 w-5 text-brand-600 dark:text-brand-400" />
            <span className="font-medium text-sm text-gray-700 dark:text-zinc-300">Forum</span>
          </Link>
        </div>

        {/* My Decks */}
        <section className="bg-white dark:bg-zinc-900 rounded-2xl border border-gray-200 dark:border-zinc-800 shadow-sm p-5 mb-6">
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center gap-2">
              <Layers className="h-4 w-4 text-indigo-400" />
              <h2 className="font-bold text-lg text-gray-900 dark:text-zinc-100">My Decks</h2>
            </div>
            <Link to="/collection" className="text-brand-600 dark:text-brand-400 text-sm font-medium hover:underline flex items-center gap-1">
              View all <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>

          {(decks as any[]).length === 0 ? (
            <div className="text-center py-6">
              <p className="text-gray-500 dark:text-zinc-500 text-sm mb-3">No decks yet — build your first competitive deck</p>
              <Link to="/collection" className="text-indigo-400 text-sm font-medium hover:underline">
                Open Deck Builder →
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
              {(decks as any[]).slice(0, 5).map((deck: any) => (
                <Link
                  key={deck.id}
                  to={`/collection/decks/${deck.id}`}
                  className="flex flex-col gap-1.5 p-3 rounded-xl border border-gray-200 dark:border-zinc-800 hover:border-indigo-500 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 dark:bg-indigo-900/20 transition-colors group"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-indigo-400 bg-indigo-100 dark:bg-indigo-900/40 px-2 py-0.5 rounded-full truncate max-w-[70%]">
                      {deck.game}
                    </span>
                    {deck.is_valid
                      ? <ShieldCheck className="h-3.5 w-3.5 text-green-500 flex-shrink-0" />
                      : <AlertTriangle className="h-3.5 w-3.5 text-amber-500 flex-shrink-0" />
                    }
                  </div>
                  <p className="font-medium text-sm text-gray-800 dark:text-zinc-200 truncate group-hover:text-indigo-700 dark:text-indigo-300">{deck.name}</p>
                  <p className="text-xs text-gray-400 dark:text-zinc-500">{deck.total_cards} cards</p>
                </Link>
              ))}
              <Link
                to="/collection"
                className="flex flex-col items-center justify-center gap-1.5 p-3 rounded-xl border border-dashed border-gray-300 dark:border-zinc-700 hover:border-indigo-500 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 dark:bg-indigo-900/20 transition-colors text-gray-400 dark:text-zinc-500 hover:text-indigo-400"
              >
                <Plus className="h-5 w-5" />
                <span className="text-xs font-medium">New Deck</span>
              </Link>
            </div>
          )}
        </section>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Recent Collection */}
          <section className="bg-white dark:bg-zinc-900 rounded-2xl border border-gray-200 dark:border-zinc-800 shadow-sm p-5">
            <div className="flex justify-between items-center mb-4">
              <div className="flex items-center gap-2">
                <Star className="h-4 w-4 text-yellow-400 fill-yellow-400" />
                <h2 className="font-bold text-lg text-gray-900 dark:text-zinc-100">Top 5</h2>
                <span className="text-sm text-gray-400 dark:text-zinc-500">{recentCards.length}/5</span>
              </div>
              <Link to="/collection" className="text-brand-600 dark:text-brand-400 text-sm font-medium hover:underline flex items-center gap-1">
                Manage <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>

            {recentCards.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-500 dark:text-zinc-500 text-sm mb-3">No cards pinned yet</p>
                <Link to="/collection" className="text-brand-600 dark:text-brand-400 text-sm font-medium hover:underline">
                  Star cards in your collection →
                </Link>
              </div>
            ) : (
              <CardFan
                cards={recentCards}
                onSell={(item) => setQuickListItem(item)}
              />
            )}
          </section>

          {/* Active Listings */}
          <section className="bg-white dark:bg-zinc-900 rounded-2xl border border-gray-200 dark:border-zinc-800 shadow-sm p-5">
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-bold text-lg text-gray-900 dark:text-zinc-100">Active Listings</h2>
              <Link to="/profile" className="text-brand-600 dark:text-brand-400 text-sm font-medium hover:underline flex items-center gap-1">
                View all <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>

            {activeListings.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-500 dark:text-zinc-500 text-sm mb-3">No active listings</p>
                <Link to="/listings/create" className="text-brand-600 dark:text-brand-400 text-sm font-medium hover:underline">Create one →</Link>
              </div>
            ) : (
              <div className="space-y-3">
                {activeListings.map((listing: any) => (
                  <div key={listing.id} className="flex items-center gap-3 p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-zinc-800 dark:bg-zinc-800 transition-colors group">
                  <Link to={`/listings/${listing.id}`} className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="h-12 w-9 rounded-lg overflow-hidden bg-gray-100 dark:bg-zinc-800 flex-shrink-0">
                      <CardImage src={listing.image_urls?.[0]} alt={listing.card?.name} cardName={listing.card?.name} cardNumber={listing.card?.card_number} game={listing.card?.game} className="w-full h-full" compact />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate text-gray-800 dark:text-zinc-200">{listing.card?.name}</p>
                      <p className="text-xs text-gray-500 dark:text-zinc-500">{listing.condition}</p>
                    </div>
                    {listing.listing_type === 'auction' ? (
                      <span className="text-sm font-bold text-orange-400">
                        {listing.current_bid ? `Top: ${formatPrice(listing.current_bid)}` : `Start: ${formatPrice(listing.starting_price ?? listing.price)}`}
                      </span>
                    ) : (listing.listing_type as string) === 'trade' ? (
                      <span className="text-xs font-semibold text-purple-400">Trade</span>
                    ) : (
                      <span className="text-sm font-bold text-brand-600 dark:text-brand-400">{formatPrice(listing.price)}</span>
                    )}
                  </Link>
                  <button
                    onClick={() => { if (window.confirm('Delete this listing?')) deleteListing(listing.id); }}
                    className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-700 dark:text-red-300 p-1 transition-all"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
      </div>

      {/* Add Card modal */}
      {showAddCard && (
        <AddCardModal
          onClose={() => setShowAddCard(false)}
          onSuccess={() => refetchCollection()}
        />
      )}

      {/* Quick List (Sell) modal */}
      {quickListItem && (
        <QuickListingModal
          item={quickListItem}
          onClose={() => setQuickListItem(null)}
          onSuccess={() => refetchCollection()}
        />
      )}
    </div>
  );
}
